import { Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { NotificationItem } from "@/lib/notification.api";
import { CATEGORY_LABELS } from "../notification.constants";
import { formatNotificationDateTime } from "../notification.formatters";
import { NotificationSeverityBadge } from "./NotificationSeverityBadge";

export function NotificationDetailDialog({
  notification,
  onOpenChange,
  onFollow,
  onDismiss,
}: {
  notification: NotificationItem | null;
  onOpenChange: (open: boolean) => void;
  onFollow: (notification: NotificationItem) => void;
  onDismiss: (id: number) => void;
}) {
  return (
    <Dialog open={Boolean(notification)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88dvh] overflow-y-auto bg-card sm:max-w-2xl">
        {notification && (
          <>
            <DialogHeader>
              <div className="flex flex-col gap-3 pr-8 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <DialogTitle className="text-[17px] font-bold text-foreground">
                    {notification.title}
                  </DialogTitle>
                  <DialogDescription className="mt-1 text-[12px] text-muted-foreground">
                    {CATEGORY_LABELS[notification.category]} -{" "}
                    {formatNotificationDateTime(notification.createdAt)}
                  </DialogDescription>
                </div>
                <NotificationSeverityBadge severity={notification.severity} />
              </div>
            </DialogHeader>

            <div className="rounded-lg border border-border bg-surface-muted px-4 py-3">
              <p className="text-[12.5px] leading-relaxed text-body">{notification.body}</p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <DetailStat label="Statut" value={notification.readAt ? "Lue" : "Non lue"} />
              <DetailStat
                label="Source"
                value={
                  notification.sourceType
                    ? `${notification.sourceType}${notification.sourceId ? ` #${notification.sourceId}` : ""}`
                    : "Système"
                }
              />
              <DetailStat
                label="Lecture"
                value={notification.readAt ? formatNotificationDateTime(notification.readAt) : "Non lue"}
              />
            </div>

            {notification.metadata && Object.keys(notification.metadata).length > 0 && (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-left">
                  <tbody>
                    {Object.entries(notification.metadata).map(([key, value]) => (
                      <tr key={key} className="border-b border-border last:border-0">
                        <td className="w-36 bg-surface-muted px-4 py-2 text-[11px] font-medium text-muted-foreground">
                          {key}
                        </td>
                        <td className="px-4 py-2 text-[12px] text-body">{String(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => onDismiss(notification.id)}>
                <Archive size={13} />
                Archiver
              </Button>
              {notification.actionUrl && (
                <Button type="button" onClick={() => onFollow(notification)}>
                  Ouvrir la source
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2">
      <p className="text-[10.5px] font-semibold uppercase tracking-wide text-subtle">{label}</p>
      <p className="mt-1 truncate text-[12px] font-medium text-body">{value}</p>
    </div>
  );
}
